/**
 * Mathtastic Limiter Group Accordion Component
 * Renders a P-type (mathtastic) limiter group as an expandable accordion
 *
 * When expanded, shows math expressions from the modified expressions file
 * instead of the individual limiters.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MathtasticIcon from '../common/MathtasticIcon';
import ConfidenceScore from '../common/ConfidenceScore';
import { LimiterGroupWithIndex } from './LimiterGroupAccordion';
import { getMathExpressionsModified, MathExpression } from '../../services/mathExpressions.service';

interface MathtasticLimiterGroupAccordionProps {
  group: LimiterGroupWithIndex;
  lessonId: string;
  onGroupChange: (groupIndex: number, selected: boolean) => void;
  onLimiterChange: (groupIndex: number, limiterIndex: number, selected: boolean) => void;
  onMathExpressionSelectionChange: (groupIndex: number, selectedIds: string[], autoSelectGroup?: boolean) => void;
  cleanLimiterDescription: (limiterDescription: string, groupDescription: string) => string;
}

export default function MathtasticLimiterGroupAccordion({
  group,
  lessonId,
  onGroupChange,
  onLimiterChange,
  onMathExpressionSelectionChange,
  cleanLimiterDescription,
}: MathtasticLimiterGroupAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [mathExpressions, setMathExpressions] = useState<MathExpression[]>([]);
  // Initialize from saved selections if available
  const [selectedExpressions, setSelectedExpressions] = useState<Set<string>>(
    new Set(group.selected_math_expression_ids || [])
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync selectedExpressions when group.selected_math_expression_ids changes externally
  useEffect(() => {
    setSelectedExpressions(new Set(group.selected_math_expression_ids || []));
  }, [group.selected_math_expression_ids]);

  // Fetch math expressions when accordion is expanded
  const fetchMathExpressions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getMathExpressionsModified(lessonId);
      const expressions = data.expressions || [];
      setMathExpressions(expressions);

      // Filter out stale IDs that no longer exist in the expressions
      const validIds = new Set(expressions.map((e: MathExpression) => e.contentExpression.id));
      setSelectedExpressions(prev => {
        const filtered = new Set([...prev].filter(id => validIds.has(id)));
        // If we filtered out some stale IDs, notify parent
        if (filtered.size !== prev.size) {
          onMathExpressionSelectionChange(group.originalGroupIndex, [...filtered]);
        }
        return filtered;
      });
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('No modified math expressions found for this lesson');
        setMathExpressions([]);
      } else {
        setError(err.message || 'Failed to load math expressions');
      }
    } finally {
      setIsLoading(false);
    }
  }, [lessonId, group.originalGroupIndex, onMathExpressionSelectionChange]);

  const handleAccordionChange = (_event: React.SyntheticEvent, expanded: boolean) => {
    setIsExpanded(expanded);
    if (expanded && mathExpressions.length === 0 && !error) {
      fetchMathExpressions();
    }
  };

  const handleExpressionToggle = (expressionId: string) => {
    const isSelecting = !selectedExpressions.has(expressionId);
    const next = new Set(selectedExpressions);

    if (isSelecting) {
      next.add(expressionId);
    } else {
      next.delete(expressionId);
    }

    setSelectedExpressions(next);

    // Notify parent of the math expression selection change
    // Pass autoSelectGroup=true when selecting an expression to auto-select the group
    onMathExpressionSelectionChange(group.originalGroupIndex, [...next], isSelecting);
  };

  // Suppress unused variable warnings - these will be used later
  void onLimiterChange;
  void cleanLimiterDescription;

  return (
    <Accordion
      key={group.limiter_group_id}
      expanded={isExpanded}
      onChange={handleAccordionChange}
      TransitionProps={{ timeout: 0, unmountOnExit: true }}
      sx={{
        mb: 1,
        bgcolor: group.selected ? 'rgba(99, 102, 241, 0.15)' : 'background.paper',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Checkbox
            checked={group.selected}
            onChange={(e) => {
              e.stopPropagation();
              onGroupChange(group.originalGroupIndex, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <Box sx={{ flex: 1, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {group.description}
            </Typography>
          </Box>
          <Tooltip title="Mathtastic Targets" arrow>
            <MathtasticIcon sx={{ fontSize: '1.4rem', mr: 1 }} />
          </Tooltip>
          {/* Show count of selected expressions */}
          <Chip
            label={`${selectedExpressions.size}/${mathExpressions.length || '?'}`}
            size="small"
            title={`${selectedExpressions.size} expressions selected`}
            sx={{
              height: '18px',
              fontSize: '0.7rem',
              fontWeight: 500,
              minWidth: 'auto',
              mr: 1,
              bgcolor: selectedExpressions.size === 0 ? 'grey.400' : 'primary.main',
              color: selectedExpressions.size === 0 ? 'grey.600' : 'white',
              opacity: selectedExpressions.size === 0 ? 0.7 : 1,
              '& .MuiChip-label': {
                px: 0.75,
                py: 0,
              },
              cursor: 'help',
            }}
          />
          <ConfidenceScore score={group.confidence_score} reasoning={group.reasoning} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {/* Loading state */}
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={20} />
            <Typography color="text.secondary">Loading math expressions...</Typography>
          </Box>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {/* Math expressions list */}
        {!isLoading && !error && mathExpressions.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No math expressions available
          </Typography>
        )}

        {!isLoading && mathExpressions.map((expr) => (
          <Box
            key={expr.contentExpression.id}
            sx={{
              pl: 2,
              py: 1,
              borderLeft: 2,
              borderColor: selectedExpressions.has(expr.contentExpression.id) ? 'primary.main' : 'divider',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Checkbox
                checked={selectedExpressions.has(expr.contentExpression.id)}
                onChange={() => handleExpressionToggle(expr.contentExpression.id)}
              />
              <Box sx={{ flex: 1, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {expr.contentExpression.plainText || expr.contentExpression.latex}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}
